import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import http from "../../services/http";
import { 
  FiGrid, 
  FiDollarSign, 
  FiTrendingUp, 
  FiTrendingDown,
  FiBarChart2,
  FiAward,
  FiCalendar,
  FiFilter,
  FiInbox,
  FiEye,
  FiX,
  FiTarget,
  FiLayers
} from "react-icons/fi";
import { BsArrowRepeat } from "react-icons/bs";

export const SectionBReportLayerList = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalGamesFound, setTotalGamesFound] = useState(0);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter States
  const [leagueFilter, setLeagueFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  
  // Date Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal State
  const [viewDetails, setViewDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load initial static data
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const [leagueRes, matchRes] = await Promise.all([
          getLeagues(),
          getMatches(),
        ]);
        if (leagueRes?.status) setLeagues(leagueRes.data || []);
        if (matchRes?.status) setMatches(matchRes.data || []);
      } catch (err) {
        console.error("Error loading static data", err);
        toast.error("Failed to load leagues/matches");
      }
    };
    loadStaticData();
  }, []);

  // Load report data
  useEffect(() => {
    loadData();
  }, [fromDate, toDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      let url = "points_game/b-game-report";
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      
      const queryString = params.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      const reportRes = await http.get(finalUrl);

      if (reportRes?.data?.status) {
        setData(reportRes.data.report || []);
        setSummary(reportRes.data.league_summary || null);
        setTotalGamesFound(reportRes.data.total_games_found || 0);
      }
    } catch (error) {
      toast.error("Failed to load B Game report");
      console.error("Error loading report:", error);
    } finally {
      setLoading(false);
    }
  };

  // Match dropdown filter by league
  const filteredMatches = useMemo(() => {
    if (!leagueFilter) return matches;
    return matches.filter(
      (m) => String(m.league_id) === String(leagueFilter)
    );
  }, [matches, leagueFilter]);

  // Main filter
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const g = item.game_info;
      if (!g) return false;

      const relatedMatch = matches.find(
        (m) => String(m.id) === String(g.match_id)
      );

      const matchLeague = leagueFilter
        ? relatedMatch &&
          String(relatedMatch.league_id) === String(leagueFilter)
        : true;

      const matchMatch = matchFilter
        ? String(g.match_id) === String(matchFilter)
        : true;

      const matchStatus = statusFilter
        ? g.status?.toLowerCase() === statusFilter.toLowerCase()
        : true;

      const matchSearch = search
        ? g.game_id?.toString().includes(search.toLowerCase()) ||
          g.game_id?.toString().includes(search)
        : true;

      return matchLeague && matchMatch && matchStatus && matchSearch;
    });
  }, [data, leagueFilter, matchFilter, statusFilter, search, matches]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const getMatchName = (id) => {
    if (!id) return "N/A";
    const match = matches.find((m) => String(m.id) === String(id));
    return match
      ? `${match.team_a || "Team A"} vs ${match.team_b || "Team B"}`
      : `Match ID: ${id}`;
  };

  const getLeagueName = (leagueId) => {
    if (!leagueId) return "No League";
    const league = leagues.find((l) => String(l.id) === String(leagueId));
    return league ? league.league_name : `League ID: ${leagueId}`;
  };

  const handleViewDetails = (item) => {
    setViewDetails(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setViewDetails(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'settled':
        return 'bg-success rounded-pill';
      case 'active':
        return 'bg-primary rounded-pill badge-live';
      case 'deleted':
        return 'bg-danger rounded-pill';
      default:
        return 'bg-secondary rounded-pill';
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [leagueFilter, matchFilter, statusFilter, search, pageSize, fromDate, toDate]);

  return (
    <div className="container-fluid px-3 px-lg-4 mt-4 mb-5">
      <div className="card shadow-premium border-0 animate-fade-in-up">
        {/* ===== HEADER ===== */}
        <div className="card-header bg-white py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-center border-bottom-soft">
          <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
            <div className="bg-primary-soft p-3 rounded-4 shadow-sm">
              <FiBarChart2 size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="mb-0 fw-bold text-dark">Section B Points Report</h4>
              <p className="text-muted small mb-0">Intellectual matrix performance analytics</p>
            </div>
          </div>
          {totalGamesFound > 0 && (
            <div className="d-flex align-items-center gap-3">
               <span className="badge bg-primary-soft text-primary px-3 py-2 rounded-pill fw-bold">
                 {totalGamesFound} Matrix Sessions Found
               </span>
               <button className="btn btn-primary-gradient px-4 rounded-pill shadow-sm" onClick={loadData} disabled={loading}>
                  <BsArrowRepeat className={`me-2 ${loading ? 'spin' : ''}`} />
                  Sync Data
               </button>
            </div>
          )}
        </div>

        <div className="card-body p-4">
          {/* ===== SUMMARY TILES ===== */}
          {summary && (
            <div className="row g-4 mb-5">
              <div className="col-xl-2 col-md-4 col-sm-6 delay-1">
                <div className="summary-card grad-blue h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Matrix Total</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_games || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiGrid className="me-1"/> Active Sessions</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-2">
                <div className="summary-card grad-red h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Total Entries</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_entries || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiTarget className="me-1"/> Participation</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-3">
                <div className="summary-card grad-green h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Net Collection</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_collection || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiDollarSign className="me-1"/> Revenue</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-4">
                <div className="summary-card grad-indigo h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Admin Profit</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_admin_profit || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiTrendingUp className="me-1"/> Yield</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-5">
                <div className="summary-card grad-gold h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Payouts</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_payout || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiTrendingDown className="me-1"/> User Outflow</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-6">
                <div className="summary-card grad-purple h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Winners</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_winners || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiAward className="me-1"/> Successful Users</div>
                </div>
              </div>
            </div>
          )}

          {/* ===== PREMIUM FILTERS ===== */}
          <div className="premium-filter-bar p-4 rounded-4 mb-4 shadow-sm border animate-fade-in-up">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiCalendar className="me-2"/>From Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiCalendar className="me-2"/>To Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiAward className="me-2"/>League Filter</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value);
                    setMatchFilter("");
                  }}
                >
                  <option value="">All Active Leagues</option>
                  {leagues.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.league_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiGrid className="me-2"/>Match Filter</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={matchFilter}
                  onChange={(e) => setMatchFilter(e.target.value)}
                  disabled={!leagueFilter && filteredMatches.length === 0}
                >
                  <option value="">All Contextual Matches</option>
                  {filteredMatches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.team_a || "Team A"} vs {m.team_b || "Team B"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold"><FiInbox className="me-2"/>Search ID</label>
                <input
                  type="text"
                  className="form-control border-0 bg-light-soft rounded-3"
                  placeholder="Scan Game ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold"><FiFilter className="me-2"/>Status Filter</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">Check All Status</option>
                  <option value="active">Active Now</option>
                  <option value="settled">Fully Settled</option>
                  <option value="deleted">Archived/Deleted</option>
                </select>
              </div>

              <div className="col-md-4 d-flex align-items-end">
                 <button className="btn btn-primary w-100 rounded-pill py-2 shadow-sm fw-bold" onClick={loadData}>
                    <FiFilter className="me-2"/> Run Analytical Scan
                 </button>
              </div>
            </div>
          </div>

          {/* ===== LOADING STATE ===== */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary shadow-sm" role="status" style={{width: '3rem', height: '3rem'}}>
                <span className="visually-hidden">Scanning...</span>
              </div>
              <p className="mt-3 fw-medium text-primary">Scanning Matrix Databases...</p>
            </div>
          )}

          {/* ===== MODERN TABLE ===== */}
          {!loading && (
            <div className="table-responsive rounded-4 shadow-sm border overflow-hidden animate-fade-in-up">
              <table className="table table-hover align-middle mb-0 border-0">
                <thead className="bg-light-soft text-muted small fw-bold">
                  <tr>
                    <th className="ps-4 py-3">#</th>
                    <th>Game Tracking ID</th>
                    <th>Match Reference</th>
                    <th>League</th>
                    <th>Live Status</th>
                    <th className="text-end">Gross Collection</th>
                    <th className="text-end">Yield (Admin)</th>
                    <th className="text-end">Outflow (Payout)</th>
                    <th className="text-center pe-4">Detailed View</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => {
                      const gameInfo = item.game_info || {};
                      const financialSummary = item.financial_summary || {};

                      return (
                        <tr key={gameInfo.game_id || index}>
                          <td className="ps-4">
                            <span className="text-muted small">{(page - 1) * pageSize + index + 1}</span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark fw-bold px-3 py-2 border rounded-3">
                              #{gameInfo.game_id || "N/A"}
                            </span>
                          </td>
                          <td>
                            <div className="fw-bold text-dark">{getMatchName(gameInfo.match_id)}</div>
                          </td>
                          <td>
                            <div className="text-muted small fw-medium">{getLeagueName(gameInfo.league_id)}</div>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(gameInfo.status)} px-3 py-2 fw-medium shadow-sm`}>
                              {(gameInfo.status || "unknown").toUpperCase()}
                            </span>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-primary font-monospace">
                              {formatCurrency(financialSummary.total_collection || 0)}
                            </span>
                          </td>
                          <td className="text-end">
                            <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 fw-bold">
                              {formatCurrency(financialSummary.admin_profit || 0)}
                            </span>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-danger font-monospace">
                              {formatCurrency(financialSummary.total_payout || 0)}
                            </span>
                          </td>
                          <td className="text-center pe-4">
                            <button
                              className="btn btn-primary-soft btn-sm px-3 rounded-pill"
                              onClick={() => handleViewDetails(item)}
                            >
                              <FiEye className="me-2"/> View Session
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="p-5">
                          <FiInbox size={60} className="text-muted opacity-25 mb-4" />
                          <h5 className="fw-bold text-dark mb-2">Zero Records Found</h5>
                          <p className="text-muted mb-0">The analytical engine found no data matching these matrix coordinates</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ===== MODERN PAGINATION ===== */}
          {totalPages > 1 && !loading && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
              <div className="text-muted small fw-medium">
                Revealing <span className="text-dark fw-bold">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="text-dark fw-bold">{Math.min(page * pageSize, filteredData.length)}</span> of{" "}
                <span className="text-primary fw-bold font-monospace">{filteredData.length}</span> analytical points
              </div>
              
              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0 gap-1">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link border-0 rounded-3 px-3 shadow-sm"
                      onClick={() => setPage(page - 1)}
                    >
                      &laquo;
                    </button>
                  </li>

                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;

                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button className="page-link border-0 rounded-3 px-3 shadow-sm" onClick={() => setPage(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button className="page-link border-0 rounded-3 px-3 shadow-sm" onClick={() => setPage(page + 1)}>
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* ===== PREMIUM DETAILS MODAL ===== */}
      {isModalOpen && viewDetails && (
        <>
          <div className="modal fade show d-block animate-fade-in" style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: 'blur(8px)' }} onClick={closeModal}>
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content border-0 shadow-premium rounded-4 overflow-hidden">
                <div className="modal-header py-4 px-4 border-bottom-soft bg-white">
                  <div className="d-flex align-items-center gap-3">
                     <div className="bg-primary-soft p-2 rounded-3">
                        <FiTarget className="text-primary" size={20}/>
                     </div>
                     <h5 className="modal-title fw-bold text-dark mb-0">Game Session Matrix - #{viewDetails.game_info?.game_id}</h5>
                  </div>
                  <button type="button" className="btn-close shadow-none" onClick={closeModal}></button>
                </div>
                
                <div className="modal-body p-4 bg-light-soft">
                  <div className="row g-4">
                     {/* DATA TILES */}
                     <div className="col-12 col-lg-8">
                        <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden animate-fade-in-up">
                           <div className="card-header bg-white border-0 py-3 px-4">
                              <h6 className="fw-bold text-dark mb-0"><FiLayers className="me-2 text-primary"/>System Diagnostics</h6>
                           </div>
                           <div className="card-body p-4 bg-white">
                              <div className="row g-4">
                                 <div className="col-6 col-md-3">
                                    <small className="text-muted d-block mb-1">Status Class</small>
                                    <span className={`badge ${getStatusBadgeClass(viewDetails.game_info?.status)} px-3`}>
                                       {viewDetails.game_info?.status?.toUpperCase()}
                                    </span>
                                 </div>
                                 <div className="col-6 col-md-3">
                                    <small className="text-muted d-block mb-1">Entries</small>
                                    <div className="fw-bold h5 mb-0 font-monospace text-primary">{viewDetails.financial_summary?.total_entries || 0}</div>
                                 </div>
                                 <div className="col-6 col-md-3">
                                    <small className="text-muted d-block mb-1">Unique Users</small>
                                    <div className="fw-bold h5 mb-0 font-monospace text-dark">{viewDetails.financial_summary?.total_users || 0}</div>
                                 </div>
                                 <div className="col-6 col-md-3">
                                    <small className="text-muted d-block mb-1">Winners</small>
                                    <div className="fw-bold h5 mb-0 font-monospace text-success">{viewDetails.financial_summary?.winner_count || 0}</div>
                                 </div>
                                 <div className="col-12">
                                    <hr className="opacity-10" />
                                 </div>
                                 <div className="col-md-6 border-end-md">
                                    <small className="text-muted d-block mb-1">Event Context</small>
                                    <div className="fw-bold text-dark">{getMatchName(viewDetails.game_info?.match_id)}</div>
                                 </div>
                                 <div className="col-md-6 ps-md-4">
                                    <small className="text-muted d-block mb-1">League Tier</small>
                                    <div className="fw-bold text-dark">{getLeagueName(viewDetails.game_info?.league_id)}</div>
                                 </div>
                                 <div className="col-md-6 border-end-md">
                                    <small className="text-muted d-block mb-1">Initialization Timestamp</small>
                                    <div className="small fw-medium">{viewDetails.game_info?.created_at ? new Date(viewDetails.game_info.created_at).toLocaleString() : "N/A"}</div>
                                 </div>
                                 <div className="col-md-6 ps-md-4">
                                    <small className="text-muted d-block mb-1">Settlement Timestamp</small>
                                    <div className="small fw-medium text-success">{viewDetails.game_info?.settled_at ? new Date(viewDetails.game_info.settled_at).toLocaleString() : "Pending Matrix Settlement"}</div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     {/* FINANCIAL SIDEBAR */}
                     <div className="col-12 col-lg-4">
                        <div className="card border-0 shadow-sm rounded-4 h-100 animate-fade-in-up delay-2">
                           <div className="card-header bg-white border-0 py-3 px-4">
                              <h6 className="fw-bold text-dark mb-0"><FiDollarSign className="me-2 text-primary"/>Economy Summary</h6>
                           </div>
                           <div className="card-body p-4 d-flex flex-column gap-4 bg-white">
                              <div className="d-flex justify-content-between align-items-center bg-light-soft p-3 rounded-3">
                                 <small className="text-muted fw-bold">GROSS COLLECTION</small>
                                 <span className="text-dark fw-bold h5 mb-0 font-monospace">{formatCurrency(viewDetails.financial_summary?.total_collection || 0)}</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center bg-primary-soft p-3 rounded-3">
                                 <small className="text-primary fw-bold">ADMIN MARGIN</small>
                                 <span className="text-primary fw-bold h5 mb-0 font-monospace">{formatCurrency(viewDetails.financial_summary?.admin_profit || 0)}</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center bg-danger-soft p-3 rounded-3">
                                 <small className="text-danger fw-bold">TOTAL OUTFLOW</small>
                                 <span className="text-danger fw-bold h5 mb-0 font-monospace">{formatCurrency(viewDetails.financial_summary?.total_payout || 0)}</span>
                              </div>
                              <div className="text-center pt-2">
                                 <div className="small text-muted mb-2">Net Distribution Index</div>
                                 <div className="progress rounded-pill bg-light shadow-inner" style={{height: '10px'}}>
                                    <div className="progress-bar bg-primary rounded-pill progress-bar-striped progress-bar-animated" role="progressbar" style={{width: `${viewDetails.financial_summary?.distribution_percentage || 0}%`}}></div>
                                 </div>
                                 <div className="mt-2 fw-bold text-primary">{viewDetails.financial_summary?.distribution_percentage || 0}%</div>
                              </div>
                           </div>
                        </div>
                     </div>

                     {/* WINNERS MATRIX */}
                     <div className="col-12 animate-fade-in-up delay-3">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                           <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                              <h6 className="fw-bold text-dark mb-0"><FiAward className="me-2 text-warning"/>Winner Distribution Matrix</h6>
                              <span className="badge bg-success bg-opacity-10 text-success rounded-pill fw-bold px-3 py-2">
                                 {viewDetails.winners_list?.length || 0} Successful Nodes
                              </span>
                           </div>
                           <div className="card-body p-0 bg-white">
                              {viewDetails.winners_list?.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light text-muted extra-small fw-bold border-0">
                                      <tr>
                                        <th className="ps-4">UID</th>
                                        <th>Participant Identity</th>
                                        <th>Mobile Endpoint</th>
                                        <th className="text-end pe-4">Credit Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {viewDetails.winners_list.map((w, i) => (
                                        <tr key={i}>
                                           <td className="ps-4">
                                              <span className="badge bg-light text-dark border fw-medium">#{w.uid || "N/A"}</span>
                                           </td>
                                          <td className="fw-bold text-dark">{w.name || "Anonymous Participant"}</td>
                                          <td className="text-muted font-monospace small">{w.mobile || "HIDDEN"}</td>
                                          <td className="text-end pe-4">
                                             <span className="text-success fw-bold font-monospace">{formatCurrency(w.win_amount)}</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="p-5 text-center">
                                   <FiTrendingDown className="text-muted opacity-25 mb-2" size={40}/>
                                   <p className="text-muted small">No participants reached winner status in this session</p>
                                </div>
                              )}
                           </div>
                        </div>
                     </div>
                  </div>
                </div>
                
                <div className="modal-footer py-3 px-4 bg-white border-top-soft">
                  <button type="button" className="btn btn-light px-5 rounded-pill shadow-sm py-2 fw-bold" onClick={closeModal}>
                     Matrix Diagnostic Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};